import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { addRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/addRelationshipMapping';
import {
  isValidClassMapping,
  isValidRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { projectClassRelationshipsByClassSelector } from 'src/selectors/projectClassRelationships';
import { projectMappingsSelector } from 'src/selectors/projectMappings';
import { projectRelationshipMappingsSelector } from 'src/selectors/projectRelationshipMappings';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useCompleteRelationshipMappingsForClassMapping = () => {
  const { projectId } = useRouterParams();

  const allClassMappings = useReduxSelector(
    projectMappingsSelector(projectId),
    isEqual
  );
  const allRelationshipMappings = useReduxSelector(
    projectRelationshipMappingsSelector(projectId),
    isEqual
  );
  const classRelationshipsByClass = useReduxSelector(
    projectClassRelationshipsByClassSelector(projectId),
    isEqual
  );

  // create relationship mappings for unmapped relationships between the current
  // class mapping and other class mappings that have valid primary identifiers
  return React.useCallback(
    (mapping: Mapping) =>
      (model: Model): boolean => {
        let didAddRelationshipMappings = false;
        if (!mapping.to) {
          return didAddRelationshipMappings;
        }

        const classIri = getModelConceptClassValue(mapping.to);
        const classRelationships = Object.values(
          classRelationshipsByClass[classIri] || []
        );
        if (!classRelationships.length) {
          return didAddRelationshipMappings;
        }

        const validClassMappingsByKey = allClassMappings.reduce<
          Record<string, Mapping & { to: string }>
        >((acc, otherMapping) => {
          if (
            // ignore invalid mappings
            isValidClassMapping(otherMapping) &&
            // ignore mappings for other resources
            otherMapping.from === mapping.from
          ) {
            acc[otherMapping.key] = otherMapping as Mapping & { to: string };
          }
          return acc;
        }, {});
        const validClassMappings = Object.values(validClassMappingsByKey);

        const otherRelationshipMappings = allRelationshipMappings.filter(
          (relMapping) => {
            const classKey = relMapping.from || relMapping.to;
            if (!classKey) {
              return false;
            }

            const classMapping = validClassMappingsByKey[classKey];
            if (
              // ignore invalid mappings, mappings for other resources
              !classMapping ||
              !isValidRelationshipMapping(relMapping, classMapping)
            ) {
              return false;
            }

            if (relMapping.from) {
              const fromClassIri = getModelConceptClassValue(classMapping.to);
              if (
                // ignore circular mappings
                fromClassIri !== relMapping.toClassIri &&
                // ignore relationship mappings not relevant to this class
                (fromClassIri === classIri ||
                  relMapping.toClassIri === classIri)
              ) {
                return true;
              }
            }

            if (relMapping.to) {
              const toClassIri = getModelConceptClassValue(classMapping.to);
              if (
                // ignore circular mappings
                toClassIri !== relMapping.fromClassIri &&
                // ignore relationship mappings not relevant to this class
                (toClassIri === classIri ||
                  relMapping.fromClassIri === classIri)
              ) {
                return true;
              }
            }

            return false;
          }
        );

        classRelationships.forEach(
          ({ iri: relationshipIri, domains, ranges }) => {
            const mappedDomainClassIris = new Set<string>();
            const mappedRangeClassIris = new Set<string>();

            otherRelationshipMappings.forEach((relMapping) => {
              if (relMapping.relationshipIri !== relationshipIri) {
                return;
              }

              if (relMapping.from && relMapping.toClassIri) {
                mappedRangeClassIris.add(relMapping.toClassIri);

                const classMapping = validClassMappingsByKey[relMapping.from];
                const fromClassIri = getModelConceptClassValue(classMapping.to);
                mappedDomainClassIris.add(fromClassIri);
              }

              if (relMapping.to && relMapping.fromClassIri) {
                mappedDomainClassIris.add(relMapping.fromClassIri);

                const classMapping = validClassMappingsByKey[relMapping.to];
                const toClassIri = getModelConceptClassValue(classMapping.to);
                mappedRangeClassIris.add(toClassIri);
              }
            });

            domains.forEach((domain) => {
              //  ignore circular mappings, duplicate mappings
              if (classIri === domain || mappedDomainClassIris.has(domain)) {
                return;
              }

              const validOpposingClassMapping = validClassMappings.find(
                (otherMapping) =>
                  getModelConceptClassValue(otherMapping.to) === domain
              );
              // ignore invalid mappings, mappings for other resources
              if (validOpposingClassMapping) {
                addRelationshipMapping({
                  fromClassIri: domain,
                  fromPrimaryIdentifier:
                    validOpposingClassMapping.primaryIdentifier,
                  relationshipIri,
                  to: mapping.key,
                })(model);
                didAddRelationshipMappings = true;
              }
            });

            ranges.forEach((range) => {
              // ignore circular mappings, duplicate mappings
              if (classIri === range || mappedRangeClassIris.has(range)) {
                return;
              }

              const validOpposingClassMapping = validClassMappings.find(
                (otherMapping) =>
                  getModelConceptClassValue(otherMapping.to) === range
              );
              // ignore invalid mappings, mappings for other resources
              if (validOpposingClassMapping) {
                addRelationshipMapping({
                  from: mapping.key,
                  relationshipIri,
                  toClassIri: range,
                  toPrimaryIdentifier:
                    validOpposingClassMapping.primaryIdentifier,
                })(model);
                didAddRelationshipMappings = true;
              }
            });
          }
        );

        return didAddRelationshipMappings;
      },
    [allClassMappings, allRelationshipMappings, classRelationshipsByClass]
  );
};
